import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, aed } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Warning, Trash } from "@phosphor-icons/react";
import { DispatchInvoiceViewDialog, chargeLabel, downloadDispatchInvoicePdf } from "@/components/DispatchInvoiceView";

export default function DispatchInvoices() {
  const { user } = useAuth();
  const { id: routeId } = useParams();
  const [params] = useSearchParams();
  const isAdmin = user?.role === "admin";
  const isDispatch = user?.role === "dispatch";
  const [rows, setRows] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [category, setCategory] = useState("transportation");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const q = (params.get("q") || "").trim().toLowerCase();

  const load = () => api.get("/dispatch-invoices").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!rows.length) return;
    const wanted = routeId ? rows.find((r) => r.id === routeId) : q ? rows.find((r) => (r.dispatch_invoice_number || "").toLowerCase() === q) : null;
    if (wanted) setViewing(wanted);
  }, [rows, routeId, q]);

  const createInvoice = async () => {
    if (!Number(amount) || Number(amount) <= 0) return toast.error("Enter an amount greater than zero");
    setBusy(true);
    try {
      await api.post("/dispatch-invoices", { category, amount: Number(amount), notes });
      toast.success("Invoice created (AED)");
      setCreateOpen(false);
      setCategory("transportation"); setAmount(""); setNotes("");
      load();
    } catch (e) { const det = e?.response?.data?.detail; toast.error(typeof det === "string" ? det : det?.message || "Failed"); }
    finally { setBusy(false); }
  };

  const canEdit = (r) => (isAdmin || user?.role === "team") || (isDispatch && r.issued_by === user?.user_id && r.status !== "paid");
  const openEdit = (r) => { setEditing(r); setCategory(r.charge_type); setAmount(String(r.amount)); setNotes(r.notes || ""); setViewing(null); };
  const saveEdit = async () => {
    if (!Number(amount) || Number(amount) <= 0) return toast.error("Enter an amount greater than zero");
    setBusy(true);
    try {
      await api.patch(`/dispatch-invoices/${editing.id}`, { category, amount: Number(amount), notes });
      toast.success("Invoice updated");
      setEditing(null);
      setCategory("transportation"); setAmount(""); setNotes("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const markPaid = async (id) => {
    try { await api.patch(`/dispatch-invoices/${id}/paid`); toast.success("Marked paid"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.dispatch_invoice_number} (${aed(r.amount)})? This cannot be undone.`)) return;
    try { await api.delete(`/dispatch-invoices/${r.id}`); toast.success(`${r.dispatch_invoice_number} deleted`); setViewing(null); load(); loadDups(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const [dups, setDups] = useState([]);
  const loadDups = () => { if (isAdmin || user?.role === "team") api.get("/dispatch-invoices/duplicates").then((r) => setDups(r.data)).catch(() => {}); };
  useEffect(() => { loadDups(); }, [isAdmin, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps
  const afterResolve = (r) => { toast.success(r.data.deleted.length ? `Deleted ${r.data.deleted.join(", ")}` : "Nothing removed"); load(); loadDups(); };
  const resolveIdentical = async () => {
    if (!window.confirm("Remove duplicate dispatch invoices with identical amounts, keeping the earliest in each group?")) return;
    try { afterResolve(await api.post("/dispatch-invoices/duplicates/resolve", { auto_identical: true })); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resolveKeep = async (g, keep) => {
    const others = g.invoices.filter((r) => r.id !== keep.id);
    if (!window.confirm(`Keep ${keep.dispatch_invoice_number} (${aed(keep.amount)}) and delete ${others.map((r) => `${r.dispatch_invoice_number} (${aed(r.amount)})`).join(", ")}?`)) return;
    try { afterResolve(await api.post("/dispatch-invoices/duplicates/resolve", { keep_id: keep.id, remove_ids: others.map((r) => r.id) })); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const shown = q ? rows.filter((r) => [r.dispatch_invoice_number, r.order_number, r.dispatch_number, r.shipment_number].some((x) => (x || "").toLowerCase().includes(q))) : rows;

  return (
    <div className="space-y-6">
      <PageHeader subtitle="Dispatch team → Admin (AED)" title="Dispatch Invoices"
        actions={isDispatch && <Button className="rounded-sm" onClick={() => setCreateOpen(true)} data-testid="new-dispatch-invoice-btn"><Plus size={16} className="mr-2" /> New Invoice</Button>}
      />
      {q && <p className="text-sm text-muted-foreground" data-testid="dispatch-invoice-filter-note">Filtered by “{params.get("q")}” · <a href="/dispatch-invoices" className="text-primary hover:underline">show all</a></p>}
      {dups.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-2" data-testid="dispatch-invoice-duplicates">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-semibold flex items-center gap-2"><Warning size={16} weight="fill" /> {dups.length} duplicate group{dups.length > 1 ? "s" : ""} — same dispatch job / order and charge type</p>
            {isAdmin && dups.some((g) => new Set(g.invoices.map((r) => Number(r.amount).toFixed(2))).size === 1) && <Button size="sm" variant="outline" className="rounded-sm h-8 border-amber-400" onClick={resolveIdentical} data-testid="dispatch-invoice-dups-auto">Remove identical duplicates (keep earliest)</Button>}
          </div>
          {dups.map((g) => {
            const same = new Set(g.invoices.map((r) => Number(r.amount).toFixed(2))).size === 1;
            return (
              <div key={`${g.ref}-${g.charge_type}`} className="text-xs flex flex-wrap items-center gap-x-2 gap-y-1" data-testid={`dup-group-${g.invoices[0].dispatch_invoice_number}`}>
                <span className="font-medium">{chargeLabel(g.invoices[0])} · {g.invoices[0].order_number || g.invoices[0].dispatch_number || g.ref}:</span>
                {g.invoices.map((r) => <span key={r.id} className="inline-flex items-center gap-1"><button onClick={() => setViewing(r)} className="underline font-medium">{r.dispatch_invoice_number}</button> ({aed(r.amount)}){isAdmin && g.invoices.length > 1 && <button onClick={() => resolveKeep(g, r)} className="rounded-sm border border-amber-400 px-1.5 py-0.5 hover:bg-amber-100" title={`Keep ${r.dispatch_invoice_number}, delete the other${g.invoices.length > 2 ? "s" : ""}`} data-testid={`dup-keep-${r.dispatch_invoice_number}`}>keep</button>}</span>)}
                {!same && <span className="text-red-700 font-semibold">amounts differ — choose which to keep</span>}
              </div>
            );
          })}
        </div>
      )}
      <div className="border border-border rounded-md overflow-x-auto bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
              <th className="px-4 py-3 font-semibold">Invoice #</th>
              <th className="px-4 py-3 font-semibold">Order #</th>
              <th className="px-4 py-3 font-semibold">Type</th>
              <th className="px-4 py-3 font-semibold text-right">Amount</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No dispatch invoices{q ? " match this filter" : " yet"}.</td></tr>
            ) : shown.map((r, i) => (
              <tr key={r.id} className={i % 2 ? "bg-muted/30" : ""} data-testid={`dispatch-invoice-row-${r.dispatch_invoice_number}`}>
                <td className="px-4 py-3 font-medium"><button onClick={() => setViewing(r)} className="text-primary hover:underline" data-testid={`dispatch-invoice-open-${r.dispatch_invoice_number}`}>{r.dispatch_invoice_number}</button></td>
                <td className="px-4 py-3 text-muted-foreground">{r.order_number || "—"}</td>
                <td className="px-4 py-3">{chargeLabel(r)}</td>
                <td className="px-4 py-3 text-right font-semibold" data-testid={`dispatch-invoice-amount-${r.dispatch_invoice_number}`}>{aed(r.amount)}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" className="rounded-sm mr-1" onClick={() => setViewing(r)} data-testid={`dispatch-invoice-view-${r.dispatch_invoice_number}`}>View</Button>
                  <Button variant="ghost" size="sm" className="rounded-sm mr-1" onClick={() => downloadDispatchInvoicePdf(r)} data-testid={`dispatch-invoice-pdf-${r.dispatch_invoice_number}`}>PDF</Button>
                  {canEdit(r) && <Button variant="ghost" size="sm" className="rounded-sm mr-1" onClick={() => openEdit(r)} data-testid={`dispatch-invoice-edit-${r.dispatch_invoice_number}`}>Edit</Button>}
                  {isAdmin && r.status !== "paid" && <Button variant="outline" size="sm" className="rounded-sm" onClick={() => markPaid(r.id)} data-testid={`dispatch-invoice-paid-${r.dispatch_invoice_number}`}>Mark paid</Button>}
                  {isAdmin && r.status !== "paid" && <Button variant="ghost" size="sm" className="rounded-sm ml-1 text-destructive hover:text-destructive" onClick={() => remove(r)} data-testid={`dispatch-invoice-delete-${r.dispatch_invoice_number}`}><Trash size={15} /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DispatchInvoiceViewDialog invoice={viewing} onClose={() => setViewing(null)} canEdit={canEdit} onEdit={openEdit} canPay={isAdmin} onPaid={(v) => { markPaid(v.id); setViewing(null); }} canDelete={isAdmin} onDelete={remove} />

      <Dialog open={createOpen || !!editing} onOpenChange={(v) => { if (!v) { setCreateOpen(false); setEditing(null); } }}>
        <DialogContent data-testid={editing ? "edit-dispatch-invoice-dialog" : "create-dispatch-invoice-dialog"}>
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.dispatch_invoice_number}` : "New Dispatch Invoice (AED)"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide">Charge type</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="dispatch-invoice-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transportation">Transportation</SelectItem>
                  <SelectItem value="stationery">Stationery</SelectItem>
                  <SelectItem value="import">Import charges</SelectItem>
                  {editing && <SelectItem value="airline">Airline</SelectItem>}
                  {editing && <SelectItem value="dispatch">Dispatch</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Amount (د.إ)</Label>
              <Input type="number" min="0" step="0.01" className="mt-1.5 rounded-sm" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="dispatch-invoice-amount-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Notes (optional)</Label>
              <Textarea rows={2} className="mt-1.5 rounded-sm" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="dispatch-invoice-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-sm" onClick={editing ? saveEdit : createInvoice} disabled={busy} data-testid={editing ? "dispatch-invoice-edit-submit" : "dispatch-invoice-create-submit"}>
              {busy ? "Saving…" : editing ? `Save ${aed(Number(amount) || 0)}` : `Create ${aed(Number(amount) || 0)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
